import React from 'react';
import { Search, FilePlus, FileText, AlertTriangle, Loader2, Calendar, Check, Calculator, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Textarea } from './ui/textarea';
import { MetiersSelection, ResultatCalcul } from './SimulateurShared';

// Mode Avenant du simulateur de tarification (3 étapes : recherche, tarification, validation).
export default function SimulateurAvenantMode({
  avenantStep, setAvenantStep,
  referenceContrat, setReferenceContrat,
  searchContrat, searchLoading,
  errorMessage, contratExpire, contratParent,
  chiffreAffaires, setChiffreAffaires,
  resultatCalcul, setResultatCalcul,
  calculerTarif,
  metiersSelectionnes, toggleMetier,
  motifAvenant, setMotifAvenant,
  notesAvenant, setNotesAvenant,
  createAvenant, creatingAvenant,
  formatDate, t,
  setContratParent, setErrorMessage
}) {
  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-4 mb-6">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
              avenantStep >= s ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {s}
            </div>
            <span className={`text-sm hidden md:block ${avenantStep >= s ? 'text-purple-600 font-medium' : 'text-gray-500'}`}>
              {s === 1 ? 'Recherche' : s === 2 ? 'Tarification' : 'Validation'}
            </span>
            {s < 3 && <div className={`w-8 md:w-12 h-1 ${avenantStep > s ? 'bg-purple-600' : 'bg-gray-200'}`}></div>}
          </div>
        ))}
      </div>

      {/* Étape 1: Recherche du contrat */}
      {avenantStep === 1 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Search className="h-6 w-6 text-purple-600" />
              <CardTitle>Recherche du Contrat Initial</CardTitle>
            </div>
            <CardDescription>
              Saisissez la référence du contrat pour lequel vous souhaitez créer un avenant
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <Label htmlFor="reference">Référence Solidaire du contrat</Label>
                <Input
                  id="reference"
                  placeholder="Ex: Réf.Solidaire202512..."
                  value={referenceContrat}
                  onChange={(e) => setReferenceContrat(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchContrat()}
                  className="mt-1"
                />
              </div>
              <Button
                onClick={searchContrat}
                disabled={searchLoading || !referenceContrat.trim()}
                className="mt-7 bg-purple-600 hover:bg-purple-700"
              >
                {searchLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="ml-2">Rechercher</span>
              </Button>
            </div>

            {/* Message d'erreur */}
            {errorMessage && (
              <Alert variant="destructive" className="border-red-300 bg-red-50">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{contratExpire ? 'Contrat Expiré' : 'Attention'}</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {/* Affichage du contrat trouvé mais expiré */}
            {contratParent && contratExpire && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="h-5 w-5 text-red-600" />
                    <span className="font-medium text-red-800">Contrat trouvé (expiré)</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Référence:</span>
                      <span className="ml-2 font-mono">{contratParent.numero_police}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Raison sociale:</span>
                      <span className="ml-2">{contratParent.raison_sociale}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Date fin:</span>
                      <span className="ml-2 text-red-600 font-medium">
                        {formatDate(contratParent.date_fin || contratParent.periode_adhesion_fin)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      )}

      {/* Étape 2: Tarification */}
      {avenantStep === 2 && contratParent && (
        <div className="space-y-4">
          {/* Résumé contrat parent - SANS bouton Avenant */}
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600" />
                  <span className="font-medium text-purple-800">Contrat sélectionné</span>
                </div>
                <Badge className="bg-green-600 text-white font-semibold">Contrat Valide</Badge>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Référence:</span>
                  <p className="font-mono font-medium text-purple-700">{contratParent.numero_police}</p>
                </div>
                <div>
                  <span className="text-gray-500">Raison sociale:</span>
                  <p className="font-medium">{contratParent.raison_sociale}</p>
                </div>
                <div>
                  <span className="text-gray-500">Date fin adhésion:</span>
                  <p className="font-medium text-purple-700">
                    {formatDate(contratParent.date_fin || contratParent.periode_adhesion_fin)}
                  </p>
                </div>
              </div>
              <div className="mt-3 p-2 bg-purple-600 rounded text-sm text-white">
                <Calendar className="h-4 w-4 inline mr-1" />
                L'avenant débutera à la date de signature et se terminera le {formatDate(contratParent.date_fin || contratParent.periode_adhesion_fin)}
              </div>
            </CardContent>
          </Card>

          {/* Simulateur */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Calculator className="h-6 w-6 text-purple-600" />
                <CardTitle>Calcul de la tarification de l'avenant</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Chiffre d'affaires avec pré-remplissage du contrat parent */}
              <div>
                <Label htmlFor="ca-avenant">Chiffre d'Affaires Annuel (€)</Label>
                <div className="relative">
                  <Input
                    id="ca-avenant"
                    type="number"
                    placeholder="Ex: 500000"
                    value={chiffreAffaires}
                    onChange={(e) => {
                      setChiffreAffaires(e.target.value);
                      setResultatCalcul(null);
                    }}
                    className="mt-1 pr-32"
                  />
                  {contratParent.chiffre_affaires && chiffreAffaires !== contratParent.chiffre_affaires.toString() && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-purple-600 hover:text-purple-800 hover:bg-purple-100"
                      onClick={() => {
                        setChiffreAffaires(contratParent.chiffre_affaires.toString());
                        setResultatCalcul(null);
                      }}
                    >
                      Reprendre CA initial
                    </Button>
                  )}
                </div>
                {contratParent.chiffre_affaires && (
                  <p className="text-xs text-gray-500 mt-1">
                    CA du contrat parent: <span className="font-semibold text-purple-600">{contratParent.chiffre_affaires.toLocaleString('fr-FR')} €</span>
                  </p>
                )}
              </div>

              {/* Sélection métiers */}
              <MetiersSelection metiersSelectionnes={metiersSelectionnes} toggleMetier={toggleMetier} t={t} />

              {/* Bouton calcul dynamique */}
              <Button
                onClick={() => {
                  calculerTarif();
                  // Auto-scroll vers le résultat après calcul
                  setTimeout(() => {
                    const resultatEl = document.getElementById('resultat-avenant');
                    if (resultatEl) {
                      resultatEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, 100);
                }}
                className={`w-full transition-all duration-300 ${
                  resultatCalcul
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
                disabled={!chiffreAffaires || Object.keys(metiersSelectionnes).length === 0}
              >
                <Calculator className="mr-2 h-4 w-4" />
                {resultatCalcul ? 'Recalculer le coût de l\'avenant' : 'Calculer le coût de l\'avenant'}
              </Button>

              {/* Résultat avec ID pour scroll */}
              <div id="resultat-avenant">
                <ResultatCalcul resultatCalcul={resultatCalcul} mode="avenant" />
              </div>

              {/* Boutons navigation - apparaît automatiquement après calcul */}
              {resultatCalcul && (
                <div className="flex gap-3 pt-4 border-t animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setAvenantStep(1);
                      setContratParent(null);
                      setErrorMessage('');
                    }}
                  >
                    Retour
                  </Button>
                  <Button
                    onClick={() => setAvenantStep(3)}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    Continuer vers la validation
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Étape 3: Validation */}
      {avenantStep === 3 && contratParent && resultatCalcul && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FilePlus className="h-6 w-6 text-purple-600" />
              <CardTitle>Récapitulatif de l'avenant</CardTitle>
            </div>
            <CardDescription>
              Vérifiez les informations avant de générer l'avenant
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Référence avenant */}
            <div className="bg-purple-600 p-4 rounded-lg">
              <h4 className="font-semibold text-white mb-2">Référence de l'avenant</h4>
              <p className="font-mono text-lg text-white">
                Réf.SolidaireAV-XX/{contratParent.numero_police}
              </p>
            </div>

            {/* Infos contrat parent */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded">
                <span className="text-sm text-gray-500">Contrat de référence</span>
                <p className="font-medium">{contratParent.numero_police}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <span className="text-sm text-gray-500">Raison sociale</span>
                <p className="font-medium">{contratParent.raison_sociale}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <span className="text-sm text-gray-500">Date de début avenant</span>
                <p className="font-medium">À la signature</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <span className="text-sm text-gray-500">Date de fin</span>
                <p className="font-medium">{formatDate(contratParent.date_fin || contratParent.periode_adhesion_fin)}</p>
              </div>
            </div>

            {/* Tarification */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Tarification</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-purple-50 rounded">
                  <span className="text-sm text-gray-500">Montant de base</span>
                  <p className="font-bold text-xl">{resultatCalcul.montantHT.toFixed(2)} €</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <span className="text-sm text-gray-500">TVA (8.5%)</span>
                  <p className="font-bold text-xl">{resultatCalcul.tva.toFixed(2)} €</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded">
                  <span className="text-sm text-gray-500">Montant Global</span>
                  <p className="font-bold text-xl text-green-700">{resultatCalcul.montantTTC.toFixed(2)} €</p>
                </div>
              </div>
            </div>

            {/* Activités sélectionnées */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Activités sélectionnées ({Object.keys(metiersSelectionnes).length})</h4>
              <div className="flex flex-wrap gap-2">
                {Object.values(metiersSelectionnes).map(({ categorie, metier }) => (
                  <Badge
                    key={`${categorie}-${metier}`}
                    className="bg-purple-600 text-white font-semibold"
                  >
                    {metier}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Motif et notes */}
            <div className="border-t pt-4 space-y-4">
              <div>
                <Label>Motif de l'avenant</Label>
                <Input
                  placeholder="Ex: Extension d'activités, augmentation CA..."
                  value={motifAvenant}
                  onChange={(e) => setMotifAvenant(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes complémentaires</Label>
                <Textarea
                  placeholder="Informations supplémentaires..."
                  value={notesAvenant}
                  onChange={(e) => setNotesAvenant(e.target.value)}
                  rows={3}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Info paiement */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-900">
                Après validation du paiement, l'attestation solidaire et le reçu seront disponibles dans l'espace du membre.
              </p>
            </div>

            {/* Boutons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setAvenantStep(2)}
              >
                Retour
              </Button>
              <Button
                onClick={createAvenant}
                disabled={creatingAvenant}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {creatingAvenant ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Générer l'avenant
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
